import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { cn } from '@shared/lib/cn';

/**
 * Floating action button — a circular CTA pinned to the bottom-right of
 * the viewport, sitting above the bottom tab bar (and its safe-area
 * inset). Used for the primary "add / new" action on list screens
 * instead of a header button.
 */
export function Fab({
  onClick,
  label,
  children,
  className,
  draggable = false,
}: {
  onClick: () => void;
  /** Accessible label (also a tooltip). */
  label: string;
  children: ReactNode;
  className?: string;
  draggable?: boolean;
}): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const holdTimerRef = useRef<number | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragRectRef = useRef<{ left: number; top: number; width: number; height: number } | null>(null);
  const suppressClickUntilRef = useRef(0);
  const dragMovedRef = useRef(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (holdTimerRef.current !== null) {
        window.clearTimeout(holdTimerRef.current);
      }
    };
  }, []);

  const clearHoldTimer = () => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const endDrag = () => {
    clearHoldTimer();
    dragStartRef.current = null;
    dragRectRef.current = null;
    if (dragging) {
      if (dragMovedRef.current) {
        suppressClickUntilRef.current = window.performance.now() + 250;
      }
      dragMovedRef.current = false;
      setDragging(false);
    }
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!draggable || event.button !== 0) return;

    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragStartRef.current = { x: event.clientX, y: event.clientY };
    dragRectRef.current = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
    dragMovedRef.current = false;

    buttonRef.current?.setPointerCapture?.(event.pointerId);
    clearHoldTimer();
    holdTimerRef.current = window.setTimeout(() => {
      setDragging(true);
    }, 180);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!dragging || !dragStartRef.current || !dragRectRef.current) return;

    const margin = 12;
    const nextX = dragRectRef.current.left + (event.clientX - dragStartRef.current.x);
    const nextY = dragRectRef.current.top + (event.clientY - dragStartRef.current.y);
    dragMovedRef.current = true;

    setPosition({
      x: Math.min(
        Math.max(margin, nextX),
        window.innerWidth - dragRectRef.current.width - margin,
      ),
      y: Math.min(
        Math.max(margin, nextY),
        window.innerHeight - dragRectRef.current.height - margin,
      ),
    });
  };

  const handleClick = () => {
    if (window.performance.now() < suppressClickUntilRef.current) {
      return;
    }
    onClick();
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      aria-label={label}
      title={label}
      style={
        position
          ? {
              left: position.x,
              top: position.y,
              right: 'auto',
              bottom: 'auto',
            }
          : {
              // Clear the fixed tab bar (≈4.25rem) + the bottom safe-area inset,
              // plus a little breathing room.
              bottom: 'calc(4.25rem + env(safe-area-inset-bottom, 0px) + 1rem)',
            }
      }
      className={cn(
        'fixed right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full',
        'bg-primary-700 text-white shadow-lg shadow-black/20',
        'transition-transform duration-fast active:scale-95',
        draggable && 'cursor-grab touch-none select-none',
        dragging && 'cursor-grabbing scale-95',
        className,
      )}
    >
      {children}
    </button>
  );
}
